<template>
  <div v-if="collection.image" class="collection__image-wrapper container">
    <Picture :images="collection.image" class="collection__image" />
  </div>

  <div
    class="collection__header"
    :class="{ 'collection__header--loading': loadingMore }"
  >
    <div class="collection__info">
      <h1 class="collection__title">{{ collection.title }}</h1>
      <p class="collection__description">{{ collection.description }}</p>
    </div>
  </div>

  <FeaturedProductGrid
    v-if="shouldShowProductsGrid"
    :products="products"
    :loading="loadingMore"
  />

  <NoProducts v-else />
</template>

<script>
import { mapGetters } from "vuex";
import FeaturedProductGrid from "@/components/collection/FeaturedProductGrid.vue";
import NoProducts from "@/components/collection/NoProducts.vue";
import collectionQuery from "@/graphql/queries/Collection.gql";
import { viewCollectionEvent } from "@/helpers/events.js";
import { unwrapNodes } from "@/helpers/graphql";
import { RelayConnection } from "@/helpers/pagination";
import Picture from "@/components/Picture.vue";

export default {
  name: "CollectionPageFeatured",
  components: {
    FeaturedProductGrid,
    NoProducts,
    Picture,
  },
  inject: ["mq"],
  props: {
    collectionHandle: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      collection: {
        products: new RelayConnection(),
      },
      loadingMore: true,
    };
  },
  computed: {
    ...mapGetters("app", ["collectionPageLayout"]),
    shouldShowProductsGrid() {
      return this.loadingMore || this.products.length > 0;
    },
    products() {
      return unwrapNodes(this.collection.products?.edges);
    },
  },
  apollo: {
    collection: {
      query: collectionQuery,
      skip() {
        return !this.collectionHandle;
      },
      variables() {
        return {
          collection: this.collectionHandle,
          first: 250,
        };
      },
      result(resp) {
        const data = resp.data;
        viewCollectionEvent(data?.collection.title);
        this.loadingMore = false;
      },
    },
  },
};
</script>

<style lang="scss" scoped>
.collection__header {
  background-color: $color-grey-light;
  padding: 60px 0px 60px 0px;

  @include respond-above(md) {
    padding: 80px 0;
  }

  &--loading {
    height: 300px;
    margin-bottom: 50px;
  }

  .types {
    padding-top: 15px;
  }
}

.collection__image-wrapper {
  @include respond-below(md) {
    padding-left: 0;
    padding-right: 0;
  }

  :deep(.picture__wrapper--loading) {
    padding-bottom: 50%;
  }
}

.collection__info {
  @extend .container;

  @include respond-above(md) {
    padding: 0 0 0 40px;
  }
}

.collection__title {
  text-transform: uppercase;
  font-size: 18px;
  font-weight: normal;

  @include respond-above(md) {
    font-size: 30px;
  }
}

.collection__description {
  margin-top: 10px;
  font-size: 14px;
  font-family: $heading-font-family;

  @include respond-above(md) {
    font-size: 18px;
    max-width: 620px;
  }
}

.toolbar {
  background: $color-white;
  display: flex;
  justify-content: space-between;
  padding: 0 10px;

  @include respond-above(md) {
    justify-content: end;
    padding-right: 0;
    margin-right: -10px;
  }

  &__wrapper {
    position: relative;

    margin: 0 auto;
    max-width: 1920px;
    width: 100%;
    z-index: 1;

    @include respond-above(md) {
      position: static;
      flex: 0 1 auto;
      margin-left: 0;
    }
  }

  &__button {
    text-transform: uppercase;
    padding: 20px 10px;
    font-size: 9px;
    background: none;
    outline: none;
    border: none;
  }

  label {
    display: inline-block;
    padding: 20px 10px;
    font-size: 9px;

    input[type="radio"] {
      position: fixed;
      opacity: 0;
      pointer-events: none;

      &:checked + span {
        font-weight: bold;
      }
    }
  }
}

.radio-text-toggle {
  text-transform: uppercase;

  & + .radio-text-toggle {
    position: relative;
    &::before {
      position: absolute;
      content: "|";
      left: 0;
    }
  }

  span {
    cursor: pointer;
  }
}

.collection__bar {
  display: flex;
  justify-content: space-between;
  @extend .container;

  @include respond-above(md) {
    overflow-y: auto;
    padding: 0 20px;
  }
}
</style>
